import { create } from "zustand";
import * as botService from "../services/botService";
import { useErrorStore } from "components";
import { buildCategoryTree, flattenCategoryTree } from "utils";

// Опції вкладок
export const TAB_OPTIONS = [
  { id: "settings", toggle_name: "Черга скасувань", name: "Налаштування" },
  { id: "queue", toggle_name: "Налаштування", name: "Черга скасувань" },
];

// Дефолтні значення фільтрів менеджерів
const DEFAULT_FILTERS = {
  logic_group: "",
  is_online: null,
  sort_by: "weighted_score",
  sort_order: "asc",
};

// Глобальний стор (state manager) сторінки "Налаштування бота"
export const useBotStore = create((set, get) => ({
  // Відображення сторінки
  activeTab: "settings",
  isLoading: true,
  isArchiveLoading: false,
  isManagersLoading: false,

  // Налаштування бота
  systemSettings: [],
  roleLogic: [],

  // Черга скасувань
  cancellationQueue: [],
  archive: [],
  archivePagination: {
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 1,
  },

  // Менеджери
  managers: [],
  managersPeriod: null,
  sources: [],
  categoriesTree: [],
  categoryOptions: [],

  // Фільтри
  managersFilters: { ...DEFAULT_FILTERS },

  // Ініціаліалізує стор при першому завантаженні сторінки
  init: async () => {
    get().initFromURL();
    const catPromise = get().fetchCategories();
    await get().fetchActiveTabData();
    await catPromise;
  },

  // Зчитує параметри з URL і встановлює початковий стан
  initFromURL: () => {
    const params = new URLSearchParams(window.location.search);
    const isOnlineParam = params.get("is_online");

    set((state) => ({
      activeTab:
        TAB_OPTIONS.find((t) => t.id === params.get("tab"))?.id || "settings",
      managersFilters: {
        ...state.managersFilters,
        logic_group: params.get("logic_group") || DEFAULT_FILTERS.logic_group,
        is_online:
          isOnlineParam !== null
            ? parseInt(isOnlineParam, 10)
            : DEFAULT_FILTERS.is_online,
        sort_by: params.get("sort_by") || DEFAULT_FILTERS.sort_by,
        sort_order: params.get("sort_order") || DEFAULT_FILTERS.sort_order,
      },
    }));
  },

  // Оновлює URL-рядок браузера відповідно до поточного стану
  syncToURL: () => {
    const { activeTab, managersFilters } = get();
    const params = new URLSearchParams();

    params.set("tab", activeTab);
    const { logic_group, is_online, sort_by, sort_order } = managersFilters;
    if (logic_group) params.set("logic_group", logic_group);
    if (is_online !== null) params.set("is_online", is_online);
    if (sort_by && sort_by !== DEFAULT_FILTERS.sort_by)
      params.set("sort_by", sort_by);
    if (sort_order && sort_order !== DEFAULT_FILTERS.sort_order)
      params.set("sort_order", sort_order);

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newUrl);
  },

  // Перемикання вкладки
  setActiveTab: (tab) => {
    set({ activeTab: tab, managersFilters: { ...DEFAULT_FILTERS } });
    get().syncToURL();
    get().fetchActiveTabData();
  },

  // Зміна фільтрів менеджерів
  setManagersFilter: (key, value) => {
    set((state) => ({
      managersFilters: {
        ...state.managersFilters,
        [key]: value,
      },
    }));
    get().syncToURL();
    get().fetchManagers();
  },

  // Сортування (з логікою Toggle і скиданням)
  setSort: (columnId) => {
    const { managersFilters } = get();
    const isSameColumn = managersFilters.sort_by === columnId;
    const newOrder =
      isSameColumn && managersFilters.sort_order === "asc" ? "desc" : "asc";

    set((state) => ({
      managersFilters: {
        ...state.managersFilters,
        sort_by: columnId,
        sort_order: newOrder,
      },
    }));

    get().syncToURL();
    get().fetchManagers();
  },

  // Глобально завантажує дані залежно від активної вкладки
  fetchActiveTabData: async () => {
    const { activeTab } = get();
    set({ isLoading: true });

    try {
      const tabSpecificPromise =
        activeTab === "queue"
          ? get().fetchCancellationQueue()
          : get().fetchSettings();

      await Promise.all([
        get().fetchSources(),
        get().fetchManagers(),
        tabSpecificPromise,
      ]);
    } finally {
      set({ isLoading: false });
    }
  },

  // Завантаження налаштувань
  fetchSettings: async () => {
    try {
      const response = await botService.fetchBotSettings();

      set({
        systemSettings: response.data?.general || [],
        roleLogic: response.data?.roles || [],
      });
    } catch (err) {
      console.error("Failed to fetch bot settings:", err);
      set({ systemSettings: [], roleLogic: [] });
    }
  },

  // Завантаження черги відмов
  fetchCancellationQueue: async () => {
    try {
      const response = await botService.fetchCancellationQueue();

      set({ cancellationQueue: response.data || [] });
    } catch (err) {
      console.error("Failed to fetch cancellation queue:", err);
      set({ cancellationQueue: [] });
    }
  },

  // Завантаження архіву скасувань
  fetchArchive: async (page = 1) => {
    try {
      const { archivePagination } = get();
      const response = await botService.fetchCancellationArchive(
        page,
        archivePagination.pageSize,
      );

      set({
        archive: response.data || [],
        archivePagination: {
          ...archivePagination,
          page: response.meta?.page || page,
          total: response.meta?.total || 0,
          totalPages: response.meta?.totalPages || 1,
        },
      });
    } catch (err) {
      console.error("Failed to fetch archive:", err);
      set({ archive: [] });
    }
  },

  // Завантаження менеджерів
  fetchManagers: async () => {
    set({ isManagersLoading: true });
    try {
      const { managersFilters } = get();
      const response = await botService.fetchManagers(managersFilters);

      set({
        managers: Array.isArray(response.managers) ? response.managers : [],
        managersPeriod: response.period || null,
      });
    } catch (err) {
      console.error("Failed to fetch managers:", err);
      set({ managers: [], managersPeriod: null });
    } finally {
      set({ isManagersLoading: false });
    }
  },

  // Завантаження джерел
  fetchSources: async () => {
    try {
      const data = await botService.fetchSources();

      set({ sources: Array.isArray(data) ? data : [] });
    } catch (err) {
      console.error("Failed to fetch sources:", err);
      set({ sources: [] });
    }
  },

  // Завантаження категорій
  fetchCategories: async () => {
    try {
      const data = await botService.fetchCategories();
      const categoriesArray = Array.isArray(data) ? data : (data?.data || []);
      const tree = buildCategoryTree(categoriesArray);
      const options = flattenCategoryTree(tree);
      set({ categoriesTree: tree, categoryOptions: options });
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  },

  // Оновлення налаштувань
  updateSetting: async (key, value) => {
    try {
      await botService.updateBotSetting(key, value);

      await get().fetchSettings();
      return true;
    } catch (err) {
      console.error("Failed to update setting:", err);
      useErrorStore
        .getState()
        .showError("Не вдалося зберегти налаштування. Спробуйте пізніше.");
      return false;
    }
  },

  // Створення нового запиту в чергу відмов
  createQueueRequest: async (data) => {
    try {
      await botService.createCancellationRequest(data);

      await get().fetchCancellationQueue();
      return true;
    } catch (err) {
      console.error("Failed to create cancellation request:", err);
      return false;
    }
  },

  // Видалення запиту в чергу відмов
  deleteQueueRequest: async (id) => {
    try {
      await botService.deleteCancellationRequest(id);

      await get().fetchCancellationQueue();
      return true;
    } catch (err) {
      console.error("Failed to delete cancellation request:", err);
      return false;
    }
  },

  // Оновлення статусу менеджера
  updateManagerStatus: async (id, isOnline) => {
    // Запам'ятовуємо попередній стан для відкату
    const previousManagers = get().managers;

    // Оновлюємо інтерфейс одразу
    set((state) => ({
      managers: state.managers.map((m) =>
        String(m.manager_id) === String(id)
          ? { ...m, is_online: isOnline ? 1 : 0 }
          : m,
      ),
    }));

    try {
      // Після, оновлюємо backend
      await botService.updateManagerStatus(id, isOnline);
      return true;
    } catch (err) {
      console.error("Failed to update manager status:", err);
      useErrorStore
        .getState()
        .showError("Не вдалося оновити статус менеджера. Спробуйте пізніше.");
      // Якщо сталась помилка відкатуємо фронтенд
      set({ managers: previousManagers });
      return false;
    }
  },

  // Оновлення налаштувань менеджера
  updateManagerSettings: async (id, personalSettings) => {
    try {
      await botService.updateManagerSettings(id, personalSettings);

      // Оптимстичне оновлення
      set((state) => ({
        managers: state.managers.map((m) =>
          String(m.manager_id) === String(id)
            ? { ...m, personal_settings: personalSettings }
            : m,
        ),
      }));
      return true;
    } catch (err) {
      console.error("Failed to update manager settings:", err);
      return false;
    }
  },
}));
